import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Star, Clock, MapPin, Briefcase, Mail, Phone, Globe } from "lucide-react";
import type { FlexHelper } from "@/lib/types";

interface HelperProfileCardProps {
  helper: FlexHelper;
  onAssign: (helperId: string) => void;
}

export function HelperProfileCard({ helper, onAssign }: HelperProfileCardProps) {
  return (
    <Card className="overflow-hidden transition-all hover:shadow-lg bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <div className="p-6 space-y-6">
        {/* Header Section */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex gap-6">
            {/* Avatar */}
            <div className="relative">
              <div className="w-[72px] h-[72px] rounded-full bg-primary/10 flex items-center justify-center shadow-sm">
                <span className="text-2xl font-bold text-primary">
                  {helper.name.charAt(0)}
                </span>
              </div>
              <div
                className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-slate-800 ${
                  helper.status === 'available'
                    ? 'bg-emerald-500'
                    : helper.status === 'on_task'
                    ? 'bg-amber-500'
                    : 'bg-slate-500'
                }`}
              />
            </div>

            {/* Basic Info */}
            <div className="space-y-2">
              <div className="space-y-1">
                <h3 className="text-xl font-bold tracking-tight">{helper.name}</h3>
                <div className="flex items-center gap-3">
                  <Badge 
                    variant={helper.status === 'available' ? 'default' : 'secondary'}
                    className="font-medium text-sm"
                  >
                    {helper.status === 'available' ? 'Available Now' : 'On Task'}
                  </Badge>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 fill-primary text-primary" />
                        <span className="font-semibold">4.8</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>124 completed tasks</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
                <div className="flex items-center gap-1.5">
                  <Briefcase className="h-4 w-4" />
                  <span>HVAC Specialist</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Globe className="h-4 w-4" />
                  <span>5+ years experience</span>
                </div>
              </div>
            </div>
          </div>

          {/* Assign Task Button */}
          <Button
            size="lg"
            className="hidden sm:flex transition-all hover:scale-105 hover:shadow-md"
            onClick={() => onAssign(helper.id)}
          >
            Assign Task
          </Button>
        </div>

        {/* Current Task (if any) */}
        {helper.currentTask && (
          <div className="rounded-lg bg-slate-100 dark:bg-slate-800 p-4 border border-slate-200 dark:border-slate-700">
            <div className="font-medium mb-1">Current Task</div>
            <p className="text-sm text-slate-600 dark:text-slate-400">{helper.currentTask}</p>
          </div>
        )}

        {/* Status Info Grid */}
        <div className="grid grid-cols-3 gap-6 pt-4 border-t border-slate-200/80 dark:border-slate-700/80">
          <div className="space-y-1">
            <div className="text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Last active
            </div>
            <div className="text-sm">
              {new Date(helper.currentLocation?.timestamp || '').toLocaleTimeString()}
            </div>
          </div>

          <div className="space-y-1">
            <div className="text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Distance
            </div>
            <div className="text-sm">2.5 miles away</div>
          </div>

          <div className="space-y-1">
            <div className="text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Contact
            </div>
            <div className="text-sm">(555) 123-4567</div>
          </div>
        </div>

        {/* Mobile Actions */}
        <div className="flex gap-3 sm:hidden">
          <Button 
            className="flex-1 shadow-sm hover:shadow-md transition-all"
            onClick={() => onAssign(helper.id)}
          >
            Assign Task
          </Button>
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={(e) => {
              e.stopPropagation();
              window.location.href = "mailto:helper@flexhelp.com";
            }}
          >
            <Mail className="h-4 w-4 mr-2" />
            Contact
          </Button>
        </div>
      </div>
    </Card>
  );
}